import { SelectProps } from '@amzn/awsui-components-react-v3';
import _ from 'lodash';
import { Category, Subcategory } from '../../containers/ContainerManagement/types';
import { ContainerDefinition } from '../../services/container-management/types';

const DOT = '.';
const CATEGORY_DEPTH = 0;
const SUBCATEGORY_DEPTH = 1;
const CONTAINER_DEFINITION_ID = 'containerDefinitionId';
const DEFAULT = 'default';
const SEAL_ID_REQUIRED = 'sealIdRequired';
const TICKET_ID_REQUIRED = 'ticketIdRequired';
const CCTV_REQUIRED = 'cctvRequired';
const SECTION = 'section';
const AISLE = 'aisle';
const LEVEL = 'level';
const ALPHABET_IDENTIFIER = 'alphabetIdentifier';
const CONTAINER_NAME_PREFIX = 'containerNamePrefix';
const SIZE = 'size';
const VIRTUAL_BIN_TYPE = 'virtualBinType';
const VALIDATIONS_REQUIRED = 'validationsRequired';
const MOVE_SUPPORT_SUPPORTED_PARENT_LOCATIONS = 'moveSupport.supportedParentLocations';

const TRUE = 'true';

// flattens JSON response for easy parsing.
export const flattenJSON = (obj: any = {}, res: any = {}, extraKey = '') => {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (typeof obj[key] !== 'object') {
      res[extraKey + key] = obj[key];
    } else {
      flattenJSON(obj[key], res, `${extraKey}${key}.`);
    }
  }
  return res;
};

/**
 * parse ListContainerDefinitions response into two maps for ease of use
 * Input:
 *   - containerType: "bin", etc.
 *   - categories: JSON object with the structure of Category -> Subcategory -> Container Def.
 * Output:
 *   - categoriesMap: map from category name to Category, which contains
 *                    a list of Subcategories (has subcategory name and container definition ID)
 *   - containerDefinitionMap: map from container definition ID to Container Definition
 * */
export function parseListContainerDefinitionsResponse(
  containerType: string,
  categories: JSON
): [Map<string, Category>, Map<string, ContainerDefinition>, Map<string, string[]>] {
  const flattenedResponse = flattenJSON(categories);

  const containerDefinitionMapWithPath = new Map<string, ContainerDefinition>();
  const pathToContainerDefinitionID = new Map<string, string>();

  _.forOwn(flattenedResponse, (value, key) => {
    const splitValues = key.split(DOT);
    const containerDefinitionPath = splitValues.slice(0, SUBCATEGORY_DEPTH + 1).join('.');
    const category = splitValues[CATEGORY_DEPTH];
    const subcategory = splitValues[SUBCATEGORY_DEPTH];

    if (key.includes(CONTAINER_DEFINITION_ID)) {
      // if we are on the container definition id node, we should save the path to id map for later
      pathToContainerDefinitionID.set(containerDefinitionPath, value);
      return;
    }

    // start filling container definition at same path or create it if it doesn't exist
    const containerDefinition = containerDefinitionMapWithPath.get(containerDefinitionPath) ?? {
      category,
      subcategory,
      containerType,

      // TODO add logic to fill policy IDs. No use cases right now, so skipping
      policyIds: [],
      metadataConfiguration: {},
      version: 0,
    };

    if (key.includes('policyIds')) {
      containerDefinition.policyIds.push(value);
    }

    // fill metadataConfiguration values (they have "default" key)
    if (key.includes(DEFAULT)) {
      if (key.includes(SEAL_ID_REQUIRED)) {
        containerDefinition.metadataConfiguration.sealIdRequired = {
          default: isValueTrue(value),
        };
      } else if (key.includes(TICKET_ID_REQUIRED)) {
        containerDefinition.metadataConfiguration.ticketIdRequired = {
          default: isValueTrue(value),
        };
      } else if (key.includes(CCTV_REQUIRED)) {
        containerDefinition.metadataConfiguration.cctvRequired = {
          default: isValueTrue(value),
        };
      } else if (key.includes(SECTION)) {
        containerDefinition.metadataConfiguration.section = {
          default: value,
        };
      } else if (key.includes(AISLE)) {
        containerDefinition.metadataConfiguration.aisle = {
          default: value,
        };
      } else if (key.includes(LEVEL)) {
        containerDefinition.metadataConfiguration.level = {
          default: value,
        };
      } else if (key.includes(ALPHABET_IDENTIFIER)) {
        containerDefinition.metadataConfiguration.alphabetIdentifier = {
          default: value,
        };
      } else if (key.includes(VIRTUAL_BIN_TYPE)) {
        containerDefinition.metadataConfiguration.virtualBinType = {
          default: isValueTrue(value),
        };
      } else if (key.includes(VALIDATIONS_REQUIRED)) {
        containerDefinition.metadataConfiguration.validationsRequired = {
          default: isValueTrue(value),
        };
      } else if (key.includes(CONTAINER_NAME_PREFIX)) {
        containerDefinition.metadataConfiguration.containerNamePrefix = {
          default: value,
        };
      } else if (key.includes(SIZE)) {
        containerDefinition.metadataConfiguration.size = {
          default: value,
        };
      }
    } else if (key.includes(MOVE_SUPPORT_SUPPORTED_PARENT_LOCATIONS)) {
      if (!containerDefinition.metadataConfiguration.moveSupport) {
        containerDefinition.metadataConfiguration.moveSupport = {
          default: true,
          supportedParentLocations: [value],
        };
      } else {
        containerDefinition.metadataConfiguration.moveSupport.supportedParentLocations.push(value);
      }
    }

    containerDefinitionMapWithPath.set(containerDefinitionPath, containerDefinition);
  });

  // create categorySubcategoryMap using pathToContainerDefinitionID information
  const categoriesMap = new Map<string, Category>();
  pathToContainerDefinitionID.forEach((containerDefinitionId, path) => {
    const categorySubcategory = path.split('.');
    const category = categorySubcategory[0];
    const subcategory = categorySubcategory[1];

    // skip the Default bin category (user should not see it)
    if (category === 'Default' && containerType === 'bin') {
      return;
    }

    if (!categoriesMap.has(category)) {
      categoriesMap.set(category, { subcategories: [] });
    }
    // eslint-disable-next-line no-unused-expressions
    categoriesMap.get(category)?.subcategories.push({
      subcategory,
      containerDefinitionId,
    });
  });

  // create containerDefinitionIdtoPolicies map
  const containerDefinitionIdToPolicyIds = new Map<string, string[]>();
  containerDefinitionMapWithPath.forEach((definition, path) => {
    const containerDefinitionId = pathToContainerDefinitionID.get(path);
    if (containerDefinitionId) {
      containerDefinitionIdToPolicyIds.set(containerDefinitionId, definition.policyIds);
    }
  });

  // create containerDefinitionMap by replacing the path with the container definition ID
  const containerDefinitionMap = new Map<string, ContainerDefinition>();
  containerDefinitionMapWithPath.forEach((definition, path) => {
    const containerDefinitionId = pathToContainerDefinitionID.get(path);
    if (containerDefinitionId) {
      containerDefinitionMap.set(containerDefinitionId, definition);
    }
  });
  return [categoriesMap, containerDefinitionMap, containerDefinitionIdToPolicyIds];
}

export function sortOptions(data: any): any {
  return data?.sort((a: any, b: any) => {
    return a.value.localeCompare(b.value, 'en', { numeric: true });
  });
}

export const optionComparator = (a: SelectProps.Option, b: SelectProps.Option) => {
  const aLabel = a?.label ?? '';
  const bLabel = b?.label ?? '';
  if (aLabel > bLabel) return 1;
  if (aLabel < bLabel) return -1;
  return 0;
};

export const getCategoryOptions = (
  categoriesMap: Map<string, Category> | null
): SelectProps.Option[] => {
  if (!categoriesMap) {
    return [];
  }
  return Array.from(categoriesMap.keys())
    .map((key) => ({
      label: key,
      value: key,
    }))
    .sort(optionComparator);
};

export const getSubcategoryOptions = (
  categoriesMap: Map<string, Category> | null,
  selectedCategory: SelectProps.Option | null
): SelectProps.Option[] => {
  return (
    categoriesMap
      ?.get(selectedCategory?.label ?? '')
      ?.subcategories?.map((subcat: Subcategory) => ({
        label: subcat.subcategory,
        value: subcat.containerDefinitionId,
      }))
      .sort(optionComparator) ?? []
  );
};

export const isValueTrue = (value: boolean | string | null): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return TRUE === value.toLowerCase();
  }

  return false;
};
